import apiService from '@/services/ApiService';
import authService from '@/services/AuthService';
import { Logger } from '@/other/Logger';
import { workspaceService } from '@/services/workspace/WorkspaceService';

const log = new Logger('pdf:resign');

const IMG_SRC = /(<img\b[^>]*?\bsrc\s*=\s*)(["'])([\s\S]*?)\2/gi;
const BUCKET_PATH = /images\/.+$/;

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

/** The same extraction ImageView.vue:167 does when it resolves a node's src. */
export const imagePathOf = (src: string): string | null => {
  const match = src.split('?')[0].match(BUCKET_PATH);
  return match ? match[0] : null;
};

const freshUrl = async (path: string): Promise<string | null> => {
  try {
    const response =
      !workspaceService.workspaceView.isSharedMode && authService.isAuthorized()
        ? await apiService.image.getUrl(path)
        : await apiService.browse.getSharedImageUrl(path, workspaceService.workspaceView.sharedToken);
    return response.data.signedUrl;
  } catch (error) {
    log.error(`re-signing failed for ${path}: ${(error as Error).message}`);
    return null;
  }
};

/**
 * Stored tab HTML carries whatever url was signed when the tab was last saved, which may have
 * expired. The painted editor does not have this problem, because ImageView re-signs into the
 * node attrs as it resolves. Failures are left in place: the renderer's prefetch will report
 * them as skipped images rather than fail the whole export (SNAP-2257).
 */
export const resignStoredHtml = async (html: string): Promise<string> => {
  const paths = new Set<string>();
  html.replace(IMG_SRC, (match, _lead, _quote, src: string) => {
    const path = imagePathOf(src);
    if (path) paths.add(path);
    return match;
  });
  if (!paths.size) return html;

  const resolved = new Map<string, string>();
  await Promise.all(
    Array.from(paths).map(async (path) => {
      const url = await freshUrl(path);
      if (url) resolved.set(path, url);
    })
  );

  return html.replace(IMG_SRC, (match, lead: string, quote: string, src: string) => {
    const path = imagePathOf(src);
    const url = path ? resolved.get(path) : undefined;
    return url ? `${lead}${quote}${escapeAttribute(url)}${quote}` : match;
  });
};

export default resignStoredHtml;
